import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Upload, FileText, CheckCircle, AlertCircle, 
  Download, Trash2, File, Image, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StaffDocuments() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [summary, setSummary] = useState({});
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        navigate('/staff-portal');
        return;
      }

      const response = await fetch(`${API_URL}/api/staff-portal/my-documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
        setSummary({
          mandatory_complete: data.mandatory_complete,
          mandatory_uploaded: data.mandatory_uploaded,
          mandatory_total: data.mandatory_total,
          salary_hold: data.salary_hold,
          max_file_size_kb: data.max_file_size_kb,
          allowed_formats: data.allowed_formats
        });
      } else if (response.status === 401) {
        navigate('/staff-portal');
      }
    } catch (error) {
      console.error('Documents fetch error:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (documentType, file) => {
    if (!file) return;

    // Validate file size (200 KB)
    if (file.size > 200 * 1024) {
      toast.error('File too large. Maximum size: 200 KB');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PDF, JPG, PNG');
      return;
    }

    setUploading(documentType);

    try {
      const token = localStorage.getItem('staffToken');
      const formData = new FormData();
      formData.append('document_type', documentType);
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/staff-portal/upload-document`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        fetchDocuments();
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(null);
    }
  };

  const downloadDocument = async (documentId, filename) => {
    try {
      const token = localStorage.getItem('staffToken');
      const response = await fetch(`${API_URL}/api/staff-portal/download-document/${documentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Convert base64 to blob
        const byteCharacters = atob(data.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.content_type });
        
        // Download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'document';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        toast.error('Failed to download');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed');
    }
  };

  const getDocumentIcon = (docType) => {
    if (docType.includes('pan') || docType.includes('aadhaar')) {
      return <FileText className="h-6 w-6" />;
    }
    return <File className="h-6 w-6" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/staff-portal/dashboard" className="p-2 hover:bg-indigo-600 rounded-lg">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold">My Documents</h1>
              <p className="text-indigo-200 text-sm">Upload required documents</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Status Card */}
        <Card className={summary.salary_hold ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {summary.salary_hold ? (
                  <AlertCircle className="h-8 w-8 text-red-500" />
                ) : (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                )}
                <div>
                  <p className={`font-semibold ${summary.salary_hold ? 'text-red-700' : 'text-green-700'}`}>
                    {summary.salary_hold ? 'Documents Incomplete - Salary On Hold' : 'All Mandatory Documents Uploaded'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {summary.mandatory_uploaded} of {summary.mandatory_total} mandatory documents uploaded
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={fetchDocuments}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-gray-700">Document Upload Guidelines:</p>
                <ul className="mt-1 text-gray-600 space-y-1">
                  <li>• Maximum file size: <strong>200 KB</strong></li>
                  <li>• Allowed formats: <strong>PDF, JPG, PNG</strong></li>
                  <li>• Mandatory documents must be uploaded for salary processing</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents List */}
        <div className="space-y-4">
          {documents.map((doc) => (
            <Card key={doc.document_type} className={doc.mandatory && !doc.uploaded ? 'border-amber-300' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${
                      doc.uploaded ? 'bg-green-100 text-green-600' : 
                      doc.mandatory ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {getDocumentIcon(doc.document_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-800">{doc.name}</p>
                        {doc.mandatory && (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Required</span>
                        )}
                        {!doc.mandatory && (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Optional</span>
                        )}
                      </div>
                      {doc.uploaded ? (
                        <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                          <CheckCircle className="h-3 w-3" />
                          Uploaded: {doc.original_filename} ({doc.file_size_kb} KB)
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 mt-1">Not uploaded yet</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {doc.uploaded && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => downloadDocument(doc.document_id, doc.original_filename)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    
                    <input
                      type="file"
                      ref={el => fileInputRefs.current[doc.document_type] = el}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileSelect(doc.document_type, e.target.files[0])}
                    />
                    
                    <Button
                      onClick={() => fileInputRefs.current[doc.document_type]?.click()}
                      disabled={uploading === doc.document_type}
                      className={doc.uploaded ? 'bg-blue-600 hover:bg-blue-700' : 'bg-indigo-600 hover:bg-indigo-700'}
                    >
                      {uploading === doc.document_type ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          {doc.uploaded ? 'Replace' : 'Upload'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bank Details Link */}
        <Card>
          <CardContent className="p-4">
            <Link 
              to="/staff-portal/bank-details"
              className="flex items-center justify-between hover:bg-gray-50 -m-4 p-4 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 text-blue-600 rounded-lg">
                  <FileText className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Bank Details</p>
                  <p className="text-sm text-gray-500">Update your bank account information</p>
                </div>
              </div>
              <ArrowLeft className="h-5 w-5 text-gray-400 rotate-180" />
            </Link>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
